import * as Sentry from '@sentry/nextjs';
import getConfig from 'next/config';
import { IsomorphicHelpers, isClientSide } from '../helpers/IsomorphicHelpers';

const { publicRuntimeConfig } = getConfig();

const REQUEST_FAILED = 'REQUEST_FAILED';

let API_HOST: string = publicRuntimeConfig.API_HOST || process.env.API_HOST;

export interface IApiValidationError {
    errorMessage: string;
    errors: {
        property: string;
        value: string | Record<string, unknown>;
        children?: IApiValidationError['errors'];
        constraints?: Record<string, string>;
        contexts?: Record<
            string,
            {
                translationKeyV2: string;
            }
        >;
    }[];
}
interface IApiErrorReponse {
    url?: string;
    payload?: unknown;
    status?: number;
    statusText?: string;
}

interface ITrackingOptions {
    captureErrorsInSentry?: boolean;
    ignoreSentryWhenStatus?: number[];
}

interface IRequestOptions extends RequestInit {
    trackingOptions?: ITrackingOptions;
}

const trackingOptionsDefaults: ITrackingOptions = {
    captureErrorsInSentry: true,
    ignoreSentryWhenStatus: [],
};

// Custom API error to throw
export class ApiError extends Error {
    details: IApiErrorReponse;

    constructor(url: string, response?: IApiErrorReponse) {
        super();
        this.details = {};

        if (!response) {
            this.message = `Request to ${url} failed with ${REQUEST_FAILED}.`;
            return;
        }

        this.message = `Request to ${url} failed with ${response.status} ${response.statusText}. \n ${response.payload}`;
        this.details = { url, ...response };
    }
}

export const fetchResource = async <T>(
    path: string,
    userOptions: IRequestOptions = {},
): Promise<T> => {
    const { trackingOptions, ...requestOptions } = userOptions;

    // Define default options
    const defaultOptions = {};

    // Define default headers
    let userAgent;
    try {
        userAgent = isClientSide()
            ? window?.navigator?.userAgent
            : IsomorphicHelpers.request.headers['user-agent'];
    } catch (e) {
        // silent
    }
    const defaultHeaders = {
        'X-User-Agent': userAgent,
        'User-Agent': isClientSide() ? undefined : 'Dreamlines/2.0 (frontend)',
        'Content-Type': 'application/json',
    };

    const options: RequestInit = {
        // Merge options
        ...defaultOptions,
        ...requestOptions,
        // Merge headers
        headers: {
            ...defaultHeaders,
            ...requestOptions.headers,
        },
    };

    const customOptions = { ...trackingOptionsDefaults, ...trackingOptions };

    // Build Url
    const url = `${API_HOST}/${path}`;

    try {
        const response = await fetch(url, options);

        if (response.ok) {
            return response.json();
        }

        const errorData = {
            status: response.status,
            statusText: response.statusText,
            payload: '',
        };

        try {
            // Try to parse the error as JSON
            errorData.payload = await response.json();
        } catch (jsonError) {
            // If parsing as JSON fails, treat it as plain text
            errorData.payload = await response.text();
        }

        throw new ApiError(url, errorData);
    } catch (error) {
        const apiError = error instanceof ApiError ? error : new ApiError(url);

        if (!customOptions.captureErrorsInSentry) {
            return Promise.reject(apiError);
        }

        if (customOptions.ignoreSentryWhenStatus?.includes(apiError.details.status ?? 0)) {
            return Promise.reject(apiError);
        }

        Sentry.captureException(apiError, (scope) => {
            scope.setExtra('extraData', {
                url: url,
                originalError: error,
                payload: options.body,
                statusCode: apiError.details?.status,
            });
            return scope;
        });

        return Promise.reject(apiError);
    }
};
